import { useState } from 'react';
import { Box, Button, Divider, TextField, Typography, Alert } from '@mui/material';
import AppShell from './components/AppShell';
import { useAuth } from './context/AuthContext';
import api from './services/api';

const UserSettings = () => {
  const { user, logout } = useAuth();

  const [username, setUsername] = useState(user?.username || '');
  const [usernameSuccess, setUsernameSuccess] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    setUsernameError('');
    setUsernameSuccess('');
    setUsernameLoading(true);
    try {
      await api.patch('/auth/me/', { username });
      setUsernameSuccess('Username updated. Please sign in again.');
      setTimeout(() => logout(), 1500);
    } catch (err) {
      setUsernameError(err.response?.data?.username || 'Failed to update username.');
    } finally {
      setUsernameLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      await api.patch('/auth/me/', { current_password: currentPassword, new_password: newPassword });
      setPasswordSuccess('Password updated. Please sign in again.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => logout(), 1500);
    } catch (err) {
      const data = err.response?.data || {};
      setPasswordError(data.current_password || data.new_password || 'Failed to update password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <AppShell maxWidth="sm">
      <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700} mb={3}>
        User Settings
      </Typography>

      {/* Change username */}
      <Box
        component="form"
        onSubmit={handleUsernameSubmit}
        sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 3, p: 3, mb: 3 }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Change Username
        </Typography>
        <TextField
          fullWidth
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          size="small"
        />
        {usernameError && <Alert severity="error" sx={{ mt: 1.5 }}>{usernameError}</Alert>}
        {usernameSuccess && <Alert severity="success" sx={{ mt: 1.5 }}>{usernameSuccess}</Alert>}
        <Button
          type="submit"
          variant="contained"
          disabled={usernameLoading || username === user?.username}
          sx={{ mt: 2, textTransform: 'none', borderRadius: 2 }}
        >
          {usernameLoading ? 'Saving…' : 'Save username'}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Change password */}
      <Box
        component="form"
        onSubmit={handlePasswordSubmit}
        sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 3, p: 3 }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Change Password
        </Typography>
        <TextField
          fullWidth
          label="Current password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          size="small"
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          size="small"
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          size="small"
        />
        {passwordError && <Alert severity="error" sx={{ mt: 1.5 }}>{passwordError}</Alert>}
        {passwordSuccess && <Alert severity="success" sx={{ mt: 1.5 }}>{passwordSuccess}</Alert>}
        <Button
          type="submit"
          variant="contained"
          disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
          sx={{ mt: 2, textTransform: 'none', borderRadius: 2 }}
        >
          {passwordLoading ? 'Saving…' : 'Save password'}
        </Button>
      </Box>
    </AppShell>
  );
};

export default UserSettings;
